import React, { useState, useEffect } from "react"
import { Row, Col, Typography } from "antd"
import PhotoSlider from "components/common/PhotoSlider"
import { useTitle } from "hooks/useTitle"
import { useTranslation } from "react-i18next"
import { modelSelector } from "store/selectors"
import { useDispatch, useSelector } from "react-redux"
import { cleanTrips } from "store/trips"
import { guideSelector } from "store/user"
import { MediaPreview } from "ui/MediaPreview"
import { VideoPlayerModal } from "ui/VideoPlayerModal"
import { isMediaFileVideo } from "utils/media-upload"
import { PreviewGallery } from "ui/PersonalInfo/ProfilePreview/PreviewGallery"
import styles from "./index.module.css"
import { getGuideAbout } from "store/guide-profile"
import { useParams } from "react-router-dom"
import FirstLoading from "modules/loader-watchers/FirstLoading"

const { Title } = Typography

function renderPhotoGrid(photos, handleMediaClick, guideName) {
  if (photos.length === 0) return null

  const mediaPreviewProps = (photo, index) => {
    const baseProps = {
      alt: `Media ${index + 1}`,
      className: styles.mediaItem,
      wrapperClassName: styles.mediaItemWrapper,
      effect: "blur"
    }
    if (!photo?.url) {
      return {
        ...baseProps,
        media: null,
        src: "",
        placeholderSrc: null,
        onClick: () => {}
      }
    }
    return {
      ...baseProps,
      media: photo,
      src: photo.url,
      placeholderSrc: photo?.placeholder_url || null,
      onClick: () => handleMediaClick?.(photo)
    }
  }

  if (photos.length === 1) {
    return <MediaPreview {...mediaPreviewProps(photos[0], 0)} />
  }

  if (photos.length === 2) {
    return (
      <Row gutter={[8, 8]}>
        <Col xs={24} lg={12}>
          <MediaPreview {...mediaPreviewProps(photos[0], 0)} />
        </Col>
        <Col xs={24} lg={12}>
          <MediaPreview {...mediaPreviewProps(photos[1], 1)} />
        </Col>
      </Row>
    )
  }

  if (photos.length === 3) {
    return (
      <Row gutter={[8, 8]}>
        <Col xs={24} lg={12}>
          <MediaPreview {...mediaPreviewProps(photos[0], 0)} />
        </Col>
        <Col xs={24} lg={12}>
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <MediaPreview {...mediaPreviewProps(photos[1], 1)} />
            </Col>
            <Col span={24}>
              <MediaPreview {...mediaPreviewProps(photos[2], 2)} />
            </Col>
          </Row>
        </Col>
      </Row>
    )
  }

  if (photos.length <= 5) {
    return (
      <Row gutter={[10, 10]}>
        <Col xs={24} lg={12}>
          <MediaPreview {...mediaPreviewProps(photos[0], 0)} />
        </Col>
        <Col xs={24} lg={12}>
          <Row gutter={[10, 10]}>
            <Col span={12}>
              <MediaPreview {...mediaPreviewProps(photos[1], 1)} />
            </Col>
            <Col span={12}>
              <MediaPreview {...mediaPreviewProps(photos[2], 2)} />
            </Col>
            <Col span={12}>
              <MediaPreview {...mediaPreviewProps(photos[3], 3)} />
            </Col>
            {photos?.[4] && (
              <Col span={12}>
                <MediaPreview {...mediaPreviewProps(photos[4], 4)} />
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    )
  }

  // More than 5 photos: show first 5 and a 'See more photos' button
  return (
    <Row gutter={[10, 10]}>
      <Col xs={24} lg={12}>
        <MediaPreview {...mediaPreviewProps(photos[0], 0)} />
      </Col>
      <Col xs={24} lg={12}>
        <Row gutter={[10, 10]}>
          <Col span={12}>
            <MediaPreview {...mediaPreviewProps(photos[1], 1)} />
          </Col>
          <Col span={12}>
            <MediaPreview {...mediaPreviewProps(photos[2], 2)} />
          </Col>
          <Col span={12}>
            <MediaPreview {...mediaPreviewProps(photos[3], 3)} />
          </Col>
          <Col span={12}>
            <div className={styles.galleryButton}>
              <PreviewGallery name={guideName} mediaItems={photos} hideGrid />
            </div>
            <MediaPreview {...mediaPreviewProps(photos[4], 4)} />
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export default function GuideAbout() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const guideAbout = useSelector((store) => store.guideProfile.about.data)
  const loading = useSelector((store) => store.guideProfile?.about?.loading)
  const guide = useSelector(guideSelector) || {}
  const tripGuide = useSelector(modelSelector("guide"))
  useTitle(guideAbout?.display_name)

  const { slug } = useParams()

  useEffect(() => {
    dispatch(getGuideAbout(slug))
  }, [slug, dispatch])

  const media = [...(guideAbout?.photos || []), ...(guideAbout?.videos || [])]
    .filter((item) => item?.url)
    .sort((a, b) => (a?.position || 0) - (b?.position || 0))

  const [videoModalOpen, setVideoModalOpen] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState(null)

  const handleMediaClick = (media) => {
    if (isMediaFileVideo(media)) {
      setSelectedMedia(media)
      setVideoModalOpen(true)
    }
  }

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false)
    setSelectedMedia(null)
  }

  useEffect(() => {
    return () => dispatch(cleanTrips())
  })

  return (
    <FirstLoading loading={loading} name="guideProfile.about">
      <div className={styles.container}>
        {media?.length > 0 && (
          <>
            <div className={styles.desktopGrid}>
              {renderPhotoGrid(media, handleMediaClick, tripGuide?.display_name || guide?.display_name)}
            </div>
            <div className={styles.mobileSlider}>
              <PhotoSlider photos={guideAbout?.photos || []} cropped_photos={guideAbout?.cropped_photos || []} ratio="16x9" className={`${styles.photoSlider} ${styles.mt20}`} />
            </div>
          </>
        )}

        <section className={styles.aboutSection}>
          <Title level={2} className={styles.sectionTitle}>
            {t("global.about")}
          </Title>
          <div className={styles.aboutDescription}>{guideAbout?.description}</div>
        </section>

        <VideoPlayerModal open={videoModalOpen} handleClose={handleCloseVideoModal} selectedMedia={selectedMedia} />
      </div>
    </FirstLoading>
  )
}
